import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactNode } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { EntityStatus, useMobile } from '@verifime/utils';
import { ColoredChip, useEntityColor } from '../ColoredIconAndChip';
import { InfoFootnote } from '../RequestScreens/InfoFootnote/InfoFootnote';
import { StickyActionBar } from '../StickyActionBar/StickyActionBar';
import { useStatusColor } from '../Status/constant';
import StatusChip from '../Status/StatusChip';
import {
  ALREADY_SHARED_LABEL,
  CHANGE_ENTITY_TYPE_LINK,
  ENTITY_PLAN_STATUS_LABEL,
  type TEntityPlanRowStatus,
} from './entityPlanCopy';

export type TEntityPlanRow = {
  /**
   * What the row verifies, for example "Individual" or "Sole Trader"
   */
  label: string;
  /**
   * The row's name, for example "Your Individual Identity". Shown as plain text. Left out, empty
   * or blank, the row shows its label alone (the caller passes a name only when it has a valid one).
   */
  name?: string;
  /**
   * Where this row stands: still to submit, submitted, a held Verified identity, or a Draft the
   * customer will finish
   */
  status: TEntityPlanRowStatus;
  /**
   * True when this row's identity is already shared with the client: the row then says so, beside
   * its status. Left out or false, the row says nothing about sharing.
   */
  alreadyShared?: boolean;
};

export type EntityPlanAttestation = {
  /**
   * What the customer attests to, shown as plain text (see attestationLabel)
   */
  label: string;
  /**
   * Whether the tick box is ticked. The caller holds it; the panel never stores it.
   */
  checked: boolean;
  /**
   * Called with the new value when the customer ticks or un-ticks the box
   */
  onChange: (checked: boolean) => void;
};

export type EntityPlanPanelProps = {
  /**
   * The plan in order: the individual first, then the chosen entity (if any)
   */
  rows: TEntityPlanRow[];
  /**
   * The primary button label, for example "Start Individual Verification"
   */
  primaryLabel: string;
  /**
   * Called when the primary button is clicked
   */
  onPrimary: () => void;
  /**
   * Whether to offer the "I want to change entity type" link (false once the choice is locked)
   */
  showChange: boolean;
  /**
   * Called when the change link is clicked
   */
  onChange: () => void;
  /**
   * One attestation for the step (618 L2, L8). While it is supplied and unticked the primary is
   * disabled. Left out, the primary behaves as it always did. The caller decides whether the
   * customer attests at all.
   */
  attestation?: EntityPlanAttestation;
  /**
   * A footnote after the actions, as plain text (618 R7). Left out, empty or blank, nothing shows.
   */
  footerNote?: string;
  /**
   * Optional data-testid for the root element
   */
  dataTestId?: string;
};

/**
 * The submitted row reuses the catalogue's StatusChip "Pass" colouring and the row still to come
 * its "Pending" colouring, each with this screen's own label.
 */
const STATUS_CHIP_STATUS = {
  toBeSubmitted: 'Pending',
  submitted: 'Pass',
} as const;

/**
 * A Verified or Draft row has no place on the catalogue's status scale, so it takes the identity
 * cards' own colours for that status (the entity palette) in a ColoredChip, with this screen's label.
 */
const ENTITY_STATUS_BY_ROW_STATUS = {
  verified: EntityStatus.Verified,
  draft: EntityStatus.Draft,
} as const;

const EntityStatusChip = ({ status }: { status: keyof typeof ENTITY_STATUS_BY_ROW_STATUS }) => {
  const color = useEntityColor(ENTITY_STATUS_BY_ROW_STATUS[status]);
  return <ColoredChip statusColor={color} label={ENTITY_PLAN_STATUS_LABEL[status]} />;
};

const RowStatusChip = ({ status }: { status: TEntityPlanRowStatus }) => {
  if (status === 'draft' || status === 'verified') {
    return <EntityStatusChip status={status} />;
  }
  return (
    <StatusChip
      status={STATUS_CHIP_STATUS[status]}
      label={ENTITY_PLAN_STATUS_LABEL[status]}
      variant="label-only"
    />
  );
};

// ADR 71: a form field carries `data-field`. MUI's Checkbox types `inputProps` as the input's own
// attributes, which leave out data-* names, so this is typed as a plain string map.
const ATTESTATION_INPUT_PROPS: Record<string, string> = { 'data-field': 'attestation' };

/**
 * The customer's attestation: one tick box, labelled as plain text. Controlled by the caller.
 */
const Attestation = ({ attestation }: { attestation: EntityPlanAttestation }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={attestation.checked}
        onChange={(event) => attestation.onChange(event.target.checked)}
        inputProps={ATTESTATION_INPUT_PROPS}
        // The box sits level with the label's first line, as the board draws it, and keeps
        // MUI's full touch target by pulling its own padding up over the space above the label.
        sx={(theme) => ({ marginTop: `calc(-1 * ${theme.semantic.spacing.sm})` })}
      />
    }
    label={attestation.label}
    // A declared name in the label may be one long word: break inside it rather than run past the
    // screen. (overflow-wrap: anywhere also lowers the label's minimum width, so the flex row yields.)
    slotProps={{ typography: { sx: { overflowWrap: 'anywhere' } } }}
    sx={{ alignItems: 'flex-start' }}
  />
);

/**
 * StickyActionBar fades into transparent at its top, so page content would show through behind the
 * attestation's text. This gives the attestation and the actions a solid surface instead: it cancels
 * the bar's own padding at the top and the sides and puts it back inside, so nothing moves, with a
 * hairline on the top edge. Used only when there is an attestation, so a panel without one is as it
 * was. (The choose screen's phone bar does the same in its own BarPanel.)
 */
const AttestationBarSurface = ({ children }: { children: ReactNode }) => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.semantic.spacing.sm,
      marginTop: `calc(-1 * ${theme.semantic.spacing.base})`,
      marginInline: `calc(-1 * ${theme.semantic.spacing.xl})`,
      paddingTop: theme.semantic.spacing.base,
      paddingInline: theme.semantic.spacing.xl,
      backgroundColor: theme.palette.background.default,
      borderTop: `${theme.semantic.spacing['4xs']} solid ${theme.palette.divider}`,
    })}
  >
    {children}
  </Box>
);

/**
 * That the row's identity is already shared with the client. A neutral chip, so it reads as a fact
 * beside the row's status and not as a second status of its own.
 */
const AlreadySharedChip = () => (
  <StatusChip status="Pending" label={ALREADY_SHARED_LABEL} variant="label-only" />
);

const RowMarker = ({ position, status }: { position: number; status: TEntityPlanRowStatus }) => {
  // The tick takes its colour from the "Submitted" chip's source; a Verified row's green is the same.
  const submittedColor = useStatusColor(STATUS_CHIP_STATUS.submitted);

  if (status === 'submitted' || status === 'verified') {
    // Decorative (MUI sets aria-hidden): the status chip's text carries the meaning.
    return (
      <CheckCircleIcon
        sx={(theme) => ({
          width: theme.semantic.size['6xs'],
          height: theme.semantic.size['6xs'],
          color: submittedColor.fill,
        })}
      />
    );
  }
  return (
    <Avatar
      sx={(theme) => ({
        width: theme.semantic.size['6xs'],
        height: theme.semantic.size['6xs'],
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: theme.semantic.fontSize.base,
        fontWeight: theme.semantic.fontWeight.semibold,
      })}
    >
      {position}
    </Avatar>
  );
};

type PlanRowProps = {
  row: TEntityPlanRow;
  position: number;
};

/**
 * One row of the plan: its marker, its type, its name when it has one, its status, and whether
 * its identity is already shared with the client.
 */
const PlanRow = ({ row, position }: PlanRowProps) => (
  <Paper
    component="li"
    variant="outlined"
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: theme.semantic.spacing.base,
      padding: theme.semantic.spacing.base,
      borderRadius: theme.semantic.borderRadius.sm,
    })}
  >
    <RowMarker position={position} status={row.status} />
    {/* Below sm the status chip drops under the name, so the name keeps the row's width. */}
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      gap={(theme) => theme.semantic.spacing.xs}
      sx={{ flex: 1, minWidth: 0 }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.tertiary">
          {row.label}
        </Typography>
        {/* No name, no line: the row shows its type alone rather than a blank line (J12). */}
        {row.name?.trim() && (
          <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
            {row.name}
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap={(theme) => theme.semantic.spacing.xs}
      >
        <RowStatusChip status={row.status} />
        {row.alreadyShared && <AlreadySharedChip />}
      </Stack>
    </Stack>
  </Paper>
);

type PlanActionsProps = Pick<
  EntityPlanPanelProps,
  'primaryLabel' | 'onPrimary' | 'showChange' | 'onChange' | 'attestation'
>;

/**
 * The change link and the primary. From sm up they are one row: the link on the left and the
 * primary on the right (as on the choose screen), and the DOM order (link then primary) is the
 * visual order. Below sm they stack with the primary on top: column-reverse draws the primary
 * first, but the tab and reading order stays link then primary, the reverse of what is shown (an
 * accepted trade-off, as on the choose screen). The primary's auto left margin pins it to the
 * right whether or not the link renders. The primary waits for the attestation: supplied and
 * unticked disables it. With none supplied it is as it always was.
 */
const PlanActions = ({
  primaryLabel,
  onPrimary,
  showChange,
  onChange,
  attestation,
}: PlanActionsProps) => (
  <Stack
    direction={{ xs: 'column-reverse', sm: 'row' }}
    alignItems="center"
    gap={(theme) => theme.semantic.spacing.base}
  >
    {showChange && (
      <Link component="button" type="button" data-action="change-entity-type" onClick={onChange}>
        {CHANGE_ENTITY_TYPE_LINK}
      </Link>
    )}
    <Button
      variant="contained"
      data-action="start-verification"
      disabled={attestation !== undefined && !attestation.checked}
      onClick={onPrimary}
      sx={{ width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' } }}
    >
      {primaryLabel}
    </Button>
  </Stack>
);

type PhoneActionBarProps = {
  attestation: EntityPlanAttestation | undefined;
  /** Whether a footer note follows the bar. */
  hasNote: boolean;
  children: ReactNode;
};

/**
 * Below 600px the actions pin to the bottom of the viewport. The page around the panel pads its
 * content by `spacing.xl` on every side, and the view's own column adds none, so `parentPaddingX`
 * and `parentPaddingBottom` negate that padding to reach the pane edges, and `contentInset` insets
 * the actions by the same amount to keep the column of the plan above them (see the prop docs on
 * StickyActionBar for why each is explicit). The attestation sits in the bar, directly above the
 * primary, so the customer ticks it where the button it unlocks is (618 J10). With a footer note
 * after it the bar is not the last thing on the page, so it does not reach down through the page's
 * bottom padding: the note would sit under the bar's edge.
 */
const PhoneActionBar = ({ attestation, hasNote, children }: PhoneActionBarProps) => {
  const { spacing } = useTheme().semantic;
  return (
    <StickyActionBar
      parentPaddingX={spacing.xl}
      parentPaddingBottom={hasNote ? '0px' : spacing.xl}
      contentInset={spacing.xl}
    >
      {attestation ? (
        <AttestationBarSurface>
          <Attestation attestation={attestation} />
          {children}
        </AttestationBarSurface>
      ) : (
        children
      )}
    </StickyActionBar>
  );
};

/**
 * The end of the panel: the attestation (when there is one) and the actions. On a phone they
 * share the sticky bar; from sm up the attestation sits above the action row.
 */
const PlanEnd = ({
  isMobile,
  hasNote,
  ...actionProps
}: PlanActionsProps & { isMobile: boolean; hasNote: boolean }) => {
  const actions = <PlanActions {...actionProps} />;
  if (isMobile) {
    return (
      <PhoneActionBar attestation={actionProps.attestation} hasNote={hasNote}>
        {actions}
      </PhoneActionBar>
    );
  }
  return (
    <>
      {actionProps.attestation && <Attestation attestation={actionProps.attestation} />}
      {actions}
    </>
  );
};

/**
 * EntityPlanPanel is the body of the confirm screen, below the client card: a numbered plan of
 * what the customer is verifying, the button that starts the next verification and, while the
 * choice is still open, the link to change the entity type. A submitted row shows a tick in place
 * of its number; a Draft row shows its number and a Draft chip. A row with no name shows its type
 * alone. From sm up the link and the button share one row, the link on the left and the button on
 * the right; below sm the button stacks above the link.
 *
 * Two optional parts serve the joined-invite screens (618). `attestation` is one tick box above the
 * actions, controlled by the caller: while it is given and unticked the primary is disabled, and
 * the caller decides whether the customer attests at all. `footerNote` is a footnote after the
 * actions. A panel with neither renders as it always did.
 *
 * Presentational only (ADR 50): the caller builds the rows and labels (see entityPlanCopy) and
 * decides whether the choice can still change. One assumption about the page around it: below 600px
 * the actions pin to the bottom of the viewport, and the footer negates the page's padding to reach
 * the pane edges, so the page must pad its content by `spacing.xl` on every side (as
 * `EntitySelectionPageLayout` does).
 */
const EntityPlanPanelComponent = ({
  rows,
  footerNote,
  dataTestId = 'entity-plan-panel',
  ...actionProps
}: Readonly<EntityPlanPanelProps>) => {
  const isMobile = useMobile();
  const note = footerNote?.trim();

  return (
    <Stack data-testid={dataTestId} gap={(theme) => theme.semantic.spacing.lg}>
      <Box
        component="ol"
        // list-style none drops the list role in Safari/VoiceOver; restate it.
        role="list"
        sx={(theme) => ({
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.semantic.spacing.sm,
        })}
      >
        {rows.map((row, index) => (
          // Rows are positional (the plan order), so the position is a key that cannot collide.
          <PlanRow key={index} row={row} position={index + 1} />
        ))}
      </Box>

      <PlanEnd isMobile={isMobile} hasNote={Boolean(note)} {...actionProps} />
      {note && <InfoFootnote>{note}</InfoFootnote>}
    </Stack>
  );
};

export const EntityPlanPanel = withClarityTheme(EntityPlanPanelComponent);
